import React, { useEffect, useState } from "react";
import { Activity, Loader2, ArrowUp, ArrowDown, Zap, TrendingUp } from "lucide-react";
import { fetchOptionsFlow } from "@/lib/api";

const fmtVol = (n) => {
  if (n == null) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return `${n}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch { return iso; }
};

/**
 * Options Flow card for the Analysis page.
 *
 * Pulls /api/market/flow/{symbol} and lists the most-unusual contracts by
 * a vol-vs-OI heuristic. Visual cues: call (green ↑) / put (red ↓), a small
 * "UNUSUAL" badge when vol/OI ≥ 3 or volume ≥ 5K, and a header bar showing
 * call/put split + total volume.
 */
const OptionsFlowCard = ({ symbol }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!symbol) return undefined;
    let alive = true;
    setLoading(true);
    setError(null);
    fetchOptionsFlow(symbol, 20)
      .then((d) => alive && setData(d))
      .catch(() => alive && setError("fetch_error"))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [symbol]);

  const summary = data?.summary || {};
  const callVol = summary.call_volume || 0;
  const putVol = summary.put_volume || 0;
  const totalVol = summary.total_volume || (callVol + putVol);
  const callShare = totalVol > 0 ? (callVol / totalVol) * 100 : 0;

  return (
    <div
      className="bg-[#0a0a0c] border border-white/10 rounded-xl p-4 sm:p-5"
      data-testid="options-flow-card"
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-[#A98CFF]" />
            <span className="text-[10px] uppercase tracking-[0.24em] text-[#A98CFF]">
              Options flow · {symbol}
            </span>
          </div>
          <h3 className="text-sm font-medium tracking-tight mt-0.5">
            Most-active contracts right now
          </h3>
        </div>
        {!!summary.unusual_count && (
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded border border-[#FF7A33]/40 text-[#FF7A33] bg-[#FF7A33]/5"
            data-testid="flow-unusual-count"
          >
            <Zap className="w-3 h-3" /> {summary.unusual_count} unusual
          </span>
        )}
      </div>

      {/* Call / Put split bar */}
      {totalVol > 0 && (
        <div
          className="mb-3 text-[11px] text-white/55 flex items-center gap-2"
          data-testid="flow-summary"
        >
          <span className="num">{fmtVol(totalVol)}</span> total ·
          <div className="flex-1 h-1.5 rounded-full overflow-hidden bg-white/[0.05] flex">
            <div className="h-full bg-[#00C805]" style={{ width: `${callShare}%` }} />
            <div className="h-full bg-[#FF3B30]" style={{ width: `${100 - callShare}%` }} />
          </div>
          <span className="num">
            <span className="text-[#00C805]">{fmtVol(callVol)}C</span> /{" "}
            <span className="text-[#FF3B30]">{fmtVol(putVol)}P</span>
          </span>
          {summary.call_put_ratio != null && (
            <span className="text-white/40">· C/P {summary.call_put_ratio.toFixed(2)}</span>
          )}
        </div>
      )}

      {loading && (
        <div className="text-center py-8 text-white/45" data-testid="flow-loading">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </div>
      )}

      {!loading && error && (
        <div className="text-center py-6 text-[11px] text-white/45 italic">
          Couldn't load flow.
        </div>
      )}

      {!loading && data?.error === "polygon_unavailable" && (
        <div className="text-center py-6 text-[11px] text-white/45 italic">
          Options flow requires a Polygon.io API key.
        </div>
      )}

      {!loading && !error && data?.contracts?.length === 0 && (
        <div className="text-center py-6 text-[11px] text-white/45 italic">
          No notable flow detected today.
        </div>
      )}

      {!loading && !error && !!data?.contracts?.length && (
        <div className="space-y-1" data-testid="flow-list">
          {data.contracts.map((c, i) => {
            const isCall = c.type === "call";
            const ratio = c.vol_oi_ratio || 0;
            return (
              <div
                key={`${c.ticker || i}`}
                data-testid={`flow-row-${i}`}
                className={`flex items-center gap-2 px-2.5 py-1.5 rounded text-[12px] border transition ${
                  c.is_unusual
                    ? "border-[#FF7A33]/25 bg-[#FF7A33]/[0.04]"
                    : "border-white/[0.06] bg-white/[0.02]"
                }`}
              >
                {isCall ? (
                  <ArrowUp className="w-3.5 h-3.5 text-[#00C805] shrink-0" />
                ) : (
                  <ArrowDown className="w-3.5 h-3.5 text-[#FF3B30] shrink-0" />
                )}
                <div className="flex-1 min-w-0 flex items-baseline gap-2">
                  <span className="num text-white tabular-nums">
                    {isCall ? "C" : "P"} ${c.strike}
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-white/40">
                    {fmtDate(c.expiration)}
                  </span>
                  {c.mid != null && (
                    <span className="text-white/55 num text-[10.5px]">
                      mid ${c.mid.toFixed(2)}
                    </span>
                  )}
                  {c.iv != null && (
                    <span className="text-[#A98CFF]/80 num text-[10.5px]">
                      IV {c.iv.toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0 text-[11px]">
                  <span className="num text-white" data-testid={`flow-row-${i}-vol`}>
                    {fmtVol(c.volume)}
                  </span>
                  <span
                    className={`num text-[10px] ${
                      ratio >= 3 ? "text-[#FF7A33]" : ratio >= 1.5 ? "text-[#FFD60A]" : "text-white/35"
                    }`}
                    title="Volume / Open Interest"
                  >
                    × {ratio.toFixed(1)}
                  </span>
                  {c.is_unusual && (
                    <TrendingUp className="w-3 h-3 text-[#FF7A33]" />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-[10px] text-white/35 mt-3 italic">
        Heuristic only. Volume ÷ OI ≥ 3 or volume ≥ 5,000 flagged as unusual.
        Refreshes every minute.
      </p>
    </div>
  );
};

export default OptionsFlowCard;
